import { Injectable } from '@angular/core';
import { SubscribeIntentionEnum, SubscribeIntentionModel, SubscriptionClient } from '@rma/billing-api';
import {
  BillingUserContextService,
  DashboardProfileContextService,
  DashboardUserContextService,
} from '@rma/dashboard/authentication/public/user';
import { NavigationService } from '@rma/dashboard/generic/util/navigation';
import { combineLatest, Observable, of, switchMap } from 'rxjs';

export const DEFAULT_SUBSCRIBE_INTENTION_QUERY_PARAM = 'subscribeIntention';

@Injectable({
  providedIn: 'root',
})
export class SubscribeIntentionService {
  constructor(
    private readonly billingUserContextService: BillingUserContextService,
    private readonly navigationService: NavigationService,
    private readonly profileContextService: DashboardProfileContextService,
    private readonly subscriptionClient: SubscriptionClient,
    private readonly userContextService: DashboardUserContextService,
  ) {}

  public getUserSubscribeIntentions(dateFrom?: Date): Observable<SubscribeIntentionModel[]> {
    if (!dateFrom) {
      dateFrom = new Date();
      dateFrom.setMonth(dateFrom.getMonth() - 1);
    }
    return this.billingUserContextService.userContext$.pipe(
      switchMap((context) =>
        this.subscriptionClient.getSubscribeIntention(dateFrom, context.productUserType, context.productUserGroupingType, context.code),
      ),
    );
  }

  public recordUserSubscribeIntention(intention: SubscribeIntentionEnum): Observable<void> {
    return combineLatest([this.billingUserContextService.userContext$, this.userContextService.isImpersonating$]).pipe(
      switchMap(([billingContext, isImpersonating]) => {
        if (isImpersonating) {
          return of(void 0);
        }
        return this.subscriptionClient.recordSubscribeIntention(
          intention,
          billingContext.productUserType,
          billingContext.productUserGroupingType,
          billingContext.code,
        );
      }),
    );
  }

  public recordUserSubscribeIntentionIfRequired(): Observable<void> {
    const intentionParam = this.navigationService.getQueryParameter(DEFAULT_SUBSCRIBE_INTENTION_QUERY_PARAM);
    if (intentionParam === null || !(intentionParam in SubscribeIntentionEnum)) {
      return of(void 0);
    }

    // SAFETY(legacy): unreviewed, pre-dates this rule.
    const intention = intentionParam as SubscribeIntentionEnum;
    return combineLatest([this.billingUserContextService.userContext$, this.userContextService.userContext$]).pipe(
      switchMap(([billingContext, userContext]) => {
        if (userContext.isImpersonating) {
          return of(void 0);
        }
        return this.subscriptionClient.recordSubscribeIntention(
          intention,
          billingContext.productUserType,
          billingContext.productUserGroupingType,
          billingContext.code,
        );
      }),
    );
  }
}
